import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Copy, Mail, Loader2, Plus, Users, Trash2, ClipboardList, DollarSign, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', host_name: '', host_email: '', mode: 'ticketed', ticket_type_id: '',
  price_rule_type: 'comp', price_rule_value: '', cap: '', cutoff_at: '', plus_ones_max: 0,
  approval_mode: 'auto', host_pays: false, entry_instructions: '', special_instructions: '',
  host_instructions: '', internal_notes: '',
};

export function GuestListsManager({ eventId, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [lists, setLists] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [entriesList, setEntriesList] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [settlement, setSettlement] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, t] = await Promise.all([
        axios.get(`${API}/events/${eventId}/guest-lists`, { headers }),
        axios.get(`${API}/events/${eventId}/ticket-types?include_hidden=true`, { headers }).catch(() => ({ data: { ticket_types: [] } })),
      ]);
      setLists(l.data.lists || []);
      setTiers((t.data.ticket_types || t.data || []).filter(x => !x.is_addon));
    } catch { toast.error('Could not load guest lists'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (gl) => {
    setEditing(gl);
    setForm({
      name: gl.name, host_name: gl.host_name || '', host_email: gl.host_email || '', mode: gl.mode,
      ticket_type_id: gl.ticket_type_id || '', price_rule_type: gl.price_rule?.type || 'comp',
      price_rule_value: gl.price_rule?.type === 'fixed' ? (gl.price_rule.value / 100) : (gl.price_rule?.value || ''),
      cap: gl.cap || '',
      cutoff_at: gl.cutoff_at ? new Date(gl.cutoff_at).toLocaleString('sv-SE').slice(0, 16).replace(' ', 'T') : '',
      plus_ones_max: gl.plus_ones_max || 0,
      approval_mode: gl.approval_mode || 'auto', host_pays: !!gl.host_pays,
      entry_instructions: gl.entry_instructions || '', special_instructions: gl.special_instructions || '',
      host_instructions: gl.host_instructions || '', internal_notes: gl.internal_notes || '',
    });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('List name required');
    setSaving(true);
    const payload = {
      name: form.name, host_name: form.host_name, host_email: form.host_email, mode: form.mode,
      ticket_type_id: form.ticket_type_id || null,
      price_rule: {
        type: form.price_rule_type,
        value: form.price_rule_type === 'fixed'
          ? Math.round(parseFloat(form.price_rule_value || 0) * 100)
          : parseFloat(form.price_rule_value || 0),
      },
      cap: parseInt(form.cap || 0, 10),
      cutoff_at: form.cutoff_at ? new Date(form.cutoff_at).toISOString() : '',
      plus_ones_max: parseInt(form.plus_ones_max || 0, 10),
      approval_mode: form.approval_mode, host_pays: form.host_pays,
      entry_instructions: form.entry_instructions, special_instructions: form.special_instructions,
      host_instructions: form.host_instructions, internal_notes: form.internal_notes,
      draws_capacity: form.price_rule_type !== 'comp',
    };
    try {
      if (editing) await axios.put(`${API}/guest-lists/${editing.list_id}`, payload, { headers });
      else await axios.post(`${API}/events/${eventId}/guest-lists`, payload, { headers });
      toast.success(editing ? 'List updated' : 'Guest list created');
      setFormOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sendHostLink = async (gl) => {
    try {
      const r = await axios.post(`${API}/guest-lists/${gl.list_id}/send-host-link`, {}, { headers });
      toast.success(`Host link emailed to ${r.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  const viewEntries = async (gl) => {
    setEntriesList(gl); setSelectedIds([]);
    try {
      const r = await axios.get(`${API}/guest-lists/${gl.list_id}/entries`, { headers });
      setEntries(r.data.entries || []);
    } catch { toast.error('Could not load guests'); }
  };

  const bulkDecide = async (decision) => {
    if (!selectedIds.length) return toast.error('Select waitlisted/pending guests first');
    try {
      await axios.post(`${API}/guest-lists/${entriesList.list_id}/entries/bulk-decision`,
        { entry_ids: selectedIds, decision }, { headers });
      toast.success(`${decision === 'approve' ? 'Approved' : 'Declined'} ${selectedIds.length} guest(s)`);
      viewEntries(entriesList); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const viewSettlement = async (gl) => {
    try {
      const r = await axios.get(`${API}/guest-lists/${gl.list_id}/settlement`, { headers });
      setSettlement(r.data);
    } catch { toast.error('Could not load settlement'); }
  };

  const removeList = async (gl) => {
    if (!window.confirm(`Delete "${gl.name}" and all its guests?`)) return;
    try { await axios.delete(`${API}/guest-lists/${gl.list_id}`, { headers }); toast.success('List deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const statusBadge = (s) => ({
    added: 'bg-slate-500/20 text-slate-300', claimed: 'bg-emerald-500/20 text-emerald-400',
    checked_in: 'bg-indigo-500/20 text-indigo-300', waitlist: 'bg-amber-500/20 text-amber-400',
    pending_approval: 'bg-orange-500/20 text-orange-300', declined: 'bg-rose-500/20 text-rose-400',
  }[s] || 'bg-slate-500/20 text-slate-300');

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="guest-lists-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2"><ClipboardList className="h-4 w-4 text-indigo-400" /> Guest Lists</h3>
          <p className="text-xs text-muted-foreground">Host-run lists with claim links — comps, list prices, VIP tables and door lists</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="create-guest-list-btn"><Plus className="h-4 w-4 mr-1" /> New list</Button>
      </div>

      {lists.length === 0 && (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
          No guest lists yet. Create one for a host, a VIP table buyer, or your own comp list.
        </CardContent></Card>
      )}

      {lists.map(gl => (
        <Card key={gl.list_id} data-testid={`guest-list-${gl.list_id}`}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-sm">{gl.name}
                  <Badge variant="outline" className={`ml-2 text-[10px] ${gl.mode === 'ticketed' ? 'text-emerald-400 border-emerald-500/40' : 'text-amber-300 border-amber-500/40'}`}>
                    {gl.mode === 'ticketed' ? 'Ticketed' : 'Door list'}
                  </Badge>
                  {gl.status !== 'open' && <Badge variant="outline" className="ml-1 text-[10px] text-rose-400">closed</Badge>}
                  {gl.host_pays && <Badge variant="outline" className="ml-1 text-[10px] text-fuchsia-300">host pays</Badge>}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Host: {gl.host_name || '—'} {gl.host_email && `(${gl.host_email})`} · {gl.price_rule?.type === 'comp' ? 'Comp' : gl.price_rule?.type === 'percent' ? `${gl.price_rule.value}% off` : `$${(gl.price_rule?.value / 100).toFixed(2)} list price`}
                  {gl.cap > 0 && ` · Cap ${gl.cap}`}{gl.plus_ones_max > 0 && ` · +${gl.plus_ones_max} each`}
                  {gl.approval_mode === 'approval' && ' · approval required'}
                </p>
              </div>
              <div className="flex gap-3 text-center">
                {[['Added', gl.counts?.added], ['Claimed', gl.counts?.claimed], ['In', gl.counts?.checked_in],
                  ...(gl.counts?.waitlist ? [['Waitlist', gl.counts.waitlist]] : []),
                  ...(gl.counts?.pending ? [['Pending', gl.counts.pending]] : [])].map(([l, v]) => (
                  <div key={l}><p className="text-sm font-bold">{v || 0}</p><p className="text-[9px] uppercase text-muted-foreground">{l}</p></div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={() => copy(gl.claim_url, 'Claim link')} data-testid={`copy-claim-${gl.list_id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Claim link</Button>
              <Button size="sm" variant="outline" onClick={() => copy(gl.host_url, 'Host link')}><Copy className="h-3.5 w-3.5 mr-1" /> Host link</Button>
              <Button size="sm" variant="outline" onClick={() => sendHostLink(gl)} data-testid={`email-host-${gl.list_id}`}><Mail className="h-3.5 w-3.5 mr-1" /> Email host</Button>
              <Button size="sm" variant="outline" onClick={() => viewEntries(gl)} data-testid={`view-entries-${gl.list_id}`}><Users className="h-3.5 w-3.5 mr-1" /> Guests</Button>
              {gl.host_pays && <Button size="sm" variant="outline" onClick={() => viewSettlement(gl)}><DollarSign className="h-3.5 w-3.5 mr-1" /> Settlement</Button>}
              <Button size="sm" variant="ghost" onClick={() => openEdit(gl)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => removeList(gl)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Create / edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="guest-list-form">
          <DialogHeader><DialogTitle>{editing ? 'Edit guest list' : 'New guest list'}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div><Label>List name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme VIP List" data-testid="gl-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Host name</Label><Input value={form.host_name} onChange={e => setForm({ ...form, host_name: e.target.value })} data-testid="gl-host-name" /></div>
              <div><Label>Host email</Label><Input value={form.host_email} onChange={e => setForm({ ...form, host_email: e.target.value })} data-testid="gl-host-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Mode</Label>
                <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })} data-testid="gl-mode">
                  <option value="ticketed">Ticketed — guest gets a real QR ticket</option>
                  <option value="door">Door list — name at the door, no ticket</option>
                </select>
              </div>
              <div>
                <Label>Price rule</Label>
                <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.price_rule_type} onChange={e => setForm({ ...form, price_rule_type: e.target.value })} data-testid="gl-price-type">
                  <option value="comp">Comp (free)</option>
                  <option value="percent">% discount</option>
                  <option value="fixed">Fixed list price</option>
                </select>
              </div>
            </div>
            {form.price_rule_type !== 'comp' && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{form.price_rule_type === 'percent' ? 'Discount %' : 'List price ($)'}</Label>
                  <Input type="number" value={form.price_rule_value} onChange={e => setForm({ ...form, price_rule_value: e.target.value })} data-testid="gl-price-value" /></div>
                <div><Label>Applies to tier</Label>
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.ticket_type_id} onChange={e => setForm({ ...form, ticket_type_id: e.target.value })} data-testid="gl-tier">
                    <option value="">— pick tier —</option>
                    {tiers.map(t => <option key={t.ticket_type_id} value={t.ticket_type_id}>{t.name} (${t.price})</option>)}
                  </select></div>
              </div>
            )}
            {form.mode === 'ticketed' && form.price_rule_type === 'comp' && (
              <div><Label>Issue tickets from tier (optional — defaults to cheapest)</Label>
                <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.ticket_type_id} onChange={e => setForm({ ...form, ticket_type_id: e.target.value })}>
                  <option value="">Cheapest active tier</option>
                  {tiers.map(t => <option key={t.ticket_type_id} value={t.ticket_type_id}>{t.name} (${t.price})</option>)}
                </select></div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Guest cap</Label><Input type="number" value={form.cap} onChange={e => setForm({ ...form, cap: e.target.value })} placeholder="0 = none" data-testid="gl-cap" /></div>
              <div><Label>Plus-ones each</Label><Input type="number" value={form.plus_ones_max} onChange={e => setForm({ ...form, plus_ones_max: e.target.value })} data-testid="gl-plusones" /></div>
              <div><Label>Approval</Label>
                <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={form.approval_mode} onChange={e => setForm({ ...form, approval_mode: e.target.value })}>
                  <option value="auto">Auto-approve</option>
                  <option value="approval">Approval required</option>
                </select></div>
            </div>
            <div><Label>Claim cutoff (optional)</Label><Input type="datetime-local" value={form.cutoff_at} onChange={e => setForm({ ...form, cutoff_at: e.target.value })} data-testid="gl-cutoff" /></div>
            <div><Label>Entry instructions (guest sees on confirmation)</Label><Textarea rows={2} value={form.entry_instructions} onChange={e => setForm({ ...form, entry_instructions: e.target.value })} placeholder="Check in under the Acme list at the Blake St door, bring ID" /></div>
            <div><Label>Special instructions (guest sees)</Label><Textarea rows={2} value={form.special_instructions} onChange={e => setForm({ ...form, special_instructions: e.target.value })} placeholder="Dress code, arrival window…" /></div>
            <div><Label>Instructions to the host (top of host page)</Label><Textarea rows={2} value={form.host_instructions} onChange={e => setForm({ ...form, host_instructions: e.target.value })} /></div>
            <div><Label>Internal notes (organizer only)</Label><Textarea rows={2} value={form.internal_notes} onChange={e => setForm({ ...form, internal_notes: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={form.host_pays} onChange={e => setForm({ ...form, host_pays: e.target.checked })} />
              Host pays — guests claim free, you bill the host from the settlement report
            </label>
            <Button onClick={save} disabled={saving} className="w-full" data-testid="gl-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save changes' : 'Create list'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Entries dialog */}
      <Dialog open={!!entriesList} onOpenChange={(o) => !o && setEntriesList(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="gl-entries-dialog">
          <DialogHeader><DialogTitle>{entriesList?.name} — guests</DialogTitle></DialogHeader>
          {entries.some(e => ['waitlist', 'pending_approval'].includes(e.status)) && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => bulkDecide('approve')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gl-bulk-approve">Approve selected</Button>
              <Button size="sm" variant="outline" className="text-rose-400" onClick={() => bulkDecide('decline')}>Decline selected</Button>
            </div>
          )}
          <div className="space-y-1">
            {entries.length === 0 && <p className="text-xs text-muted-foreground italic">No guests yet.</p>}
            {entries.map(e => (
              <div key={e.entry_id} className="flex items-center gap-2 text-xs border-b border-border/40 py-1.5">
                {['waitlist', 'pending_approval'].includes(e.status) && (
                  <input type="checkbox" checked={selectedIds.includes(e.entry_id)}
                    onChange={() => setSelectedIds(s => s.includes(e.entry_id) ? s.filter(x => x !== e.entry_id) : [...s, e.entry_id])} />
                )}
                <span className="font-medium">{e.first_name} {e.last_name}</span>
                {e.plus_ones > 0 && <span className="text-muted-foreground">+{e.plus_ones}</span>}
                <span className="text-muted-foreground truncate max-w-[160px]">{e.email}</span>
                {e.note && <span className="text-indigo-300 truncate max-w-[140px]" title={e.note}>📝 {e.note}</span>}
                <Badge className={`ml-auto text-[9px] border-0 ${statusBadge(e.status)}`}>{(e.status || 'added').replace('_', ' ')}</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settlement dialog */}
      <Dialog open={!!settlement} onOpenChange={(o) => !o && setSettlement(null)}>
        <DialogContent className="max-w-lg" data-testid="gl-settlement-dialog">
          <DialogHeader><DialogTitle>Settlement — {settlement?.list_name}</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p className="text-xs text-muted-foreground">Bill {settlement?.host_name || 'the host'}: claims × list price</p>
            <div className="flex justify-between font-semibold"><span>{settlement?.people} guest(s) claimed × {usd(settlement?.unit_price_cents)}</span><span className="text-emerald-400">{usd(settlement?.total_cents)}</span></div>
            <div className="max-h-56 overflow-y-auto space-y-1">
              {(settlement?.rows || []).map(r => (
                <div key={r.entry_id} className="flex justify-between text-xs border-b border-border/40 py-1">
                  <span>{r.name} ({r.people})</span><span className="font-mono">{usd(r.amount_cents)}</span>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
